// Project overview grid (grouped by building) + "new foyer" dialog.
import { api } from "../api";
import { preparePlan, derivePxPerMeter, type PreparedPlan } from "../pdf";
import { openBuildingManager } from "./buildings";
import type { Building, ProjectListItem } from "../types";

interface ProjectsEls {
  screen: HTMLElement;
  grid: HTMLElement;
  empty: HTMLElement;
  btnNew: HTMLButtonElement;
  btnNewEmpty: HTMLButtonElement;
  btnManageBuildings: HTMLButtonElement;
  dialog: HTMLDialogElement;
  form: HTMLFormElement;
  name: HTMLInputElement;
  building: HTMLSelectElement;
  floor: HTMLSelectElement;
  file: HTMLInputElement;
  ratio: HTMLInputElement;
  fileHint: HTMLElement;
  previewWrap: HTMLElement;
  previewCanvas: HTMLCanvasElement;
  previewInfo: HTMLElement;
  error: HTMLElement;
  submit: HTMLButtonElement;
  cancel: HTMLButtonElement;
}

let els: ProjectsEls;
let onOpen: (id: string) => void;
let prepared: PreparedPlan | null = null;
let preparedName = "plan.png";
let buildings: Building[] = [];

export function initProjects(elements: ProjectsEls, opts: { onOpen: (id: string) => void }): void {
  els = elements;
  onOpen = opts.onOpen;

  els.btnNew.addEventListener("click", openDialog);
  els.btnNewEmpty.addEventListener("click", openDialog);
  els.btnManageBuildings.addEventListener("click", () =>
    void openBuildingManager(async () => {
      await loadBuildings();
      void renderProjectList();
    })
  );
  els.cancel.addEventListener("click", () => els.dialog.close());
  els.dialog.addEventListener("close", resetDialog);
  els.file.addEventListener("change", onFileChange);
  els.building.addEventListener("change", () => fillFloorOptions(els.building.value));
  els.form.addEventListener("submit", onSubmit);
}

async function loadBuildings(): Promise<void> {
  try {
    buildings = await api.listBuildings();
  } catch {
    buildings = [];
  }
}

/* ------------------------------ grid ------------------------------ */

export async function renderProjectList(): Promise<void> {
  await loadBuildings();
  let projects: ProjectListItem[] = [];
  try {
    projects = await api.listProjects();
  } catch (err) {
    els.grid.innerHTML = `<div class="empty-note">Server nicht erreichbar: ${
      err instanceof Error ? err.message : String(err)
    }</div>`;
    return;
  }

  els.empty.hidden = projects.length > 0;
  els.grid.replaceChildren();

  // Group by building (buildings in their sort order, then "Ohne Gebäude").
  const order = new Map(buildings.map((b, i) => [b.id, i]));
  const groups = new Map<string, ProjectListItem[]>();
  for (const p of projects) {
    const key = p.buildingId ?? "__none__";
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(p);
  }
  const keys = [...groups.keys()].sort((a, b) => {
    if (a === "__none__") return 1;
    if (b === "__none__") return -1;
    return (order.get(a) ?? 999) - (order.get(b) ?? 999);
  });

  for (const key of keys) {
    const items = groups.get(key)!;
    const bld = buildings.find((b) => b.id === key);
    const floorOrder = new Map((bld?.floors ?? []).map((f, i) => [f.id, i]));
    items.sort(
      (a, b) =>
        (floorOrder.get(a.floorId ?? "") ?? 999) - (floorOrder.get(b.floorId ?? "") ?? 999) ||
        a.name.localeCompare(b.name)
    );

    const heading = document.createElement("div");
    heading.className = "project-group-title";
    heading.textContent = bld ? bld.name : key === "__none__" ? "Ohne Gebäude" : "Gebäude";
    els.grid.appendChild(heading);

    const row = document.createElement("div");
    row.className = "project-row";
    for (const p of items) row.appendChild(projectCard(p));
    els.grid.appendChild(row);
  }
}

function projectCard(p: ProjectListItem): HTMLElement {
  const card = document.createElement("div");
  card.className = "project-card";
  card.tabIndex = 0;
  card.setAttribute("role", "button");
  const thumb = document.createElement("div");
  thumb.className = "thumb";
  if (p.planUrl) thumb.style.backgroundImage = `url("${p.planUrl}")`;
  if (p.floorName) {
    const badge = document.createElement("span");
    badge.className = "floor-badge";
    badge.textContent = p.floorName;
    thumb.appendChild(badge);
  }
  const body = document.createElement("div");
  body.className = "body";
  body.innerHTML = `<div class="title">${escapeHtml(p.name)}</div>
    <div class="meta">${p.imgWidth}×${p.imgHeight} px · geändert ${formatDate(p.updatedAt)}</div>`;
  card.append(thumb, body);
  card.addEventListener("click", () => onOpen(p.id));
  card.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen(p.id);
    }
  });
  return card;
}

/* ----------------------------- dialog ----------------------------- */

function openDialog(): void {
  resetDialog();
  fillBuildingOptions();
  els.dialog.showModal();
}

function fillBuildingOptions(): void {
  els.building.replaceChildren(new Option("— kein Gebäude —", ""));
  for (const b of buildings) els.building.appendChild(new Option(b.name, b.id));
  fillFloorOptions("");
}

function fillFloorOptions(buildingId: string): void {
  const b = buildings.find((x) => x.id === buildingId);
  els.floor.replaceChildren(new Option(b ? "— Stockwerk wählen —" : "— kein Stockwerk —", ""));
  for (const f of b?.floors ?? []) els.floor.appendChild(new Option(f.name, f.id));
  els.floor.disabled = !b;
}

function resetDialog(): void {
  els.form.reset();
  prepared = null;
  preparedName = "plan.png";
  els.previewWrap.hidden = true;
  els.previewInfo.textContent = "";
  els.error.hidden = true;
  els.error.textContent = "";
  els.submit.disabled = true;
  els.floor.disabled = true;
}

function showError(msg: string): void {
  els.error.textContent = msg;
  els.error.hidden = false;
}

async function onFileChange(): Promise<void> {
  const file = els.file.files?.[0];
  els.error.hidden = true;
  prepared = null;
  els.submit.disabled = true;
  if (!file) return;

  els.fileHint.textContent = "Wird verarbeitet…";
  try {
    prepared = await preparePlan(file, els.previewCanvas);
    const base = file.name.replace(/\.[^.]+$/, "");
    preparedName =
      prepared.mime === "image/png" && file.type === "application/pdf" ? `${base}.png` : file.name;
    els.previewWrap.hidden = false;
    els.previewInfo.textContent =
      `${prepared.width}×${prepared.height} px` + (prepared.pdfPagePt ? " · aus PDF gerendert" : "");
    if (!els.name.value.trim()) els.name.value = base;
    els.submit.disabled = false;
    els.fileHint.textContent = "PDF-Grundrisse werden im Browser gerendert und als Bild gespeichert.";
  } catch (err) {
    showError(err instanceof Error ? err.message : "Datei konnte nicht gelesen werden.");
    els.fileHint.textContent = "PDF-Grundrisse werden im Browser gerendert und als Bild gespeichert.";
  }
}

async function onSubmit(ev: SubmitEvent): Promise<void> {
  ev.preventDefault();
  if (!prepared) return;
  const name = els.name.value.trim();
  if (!name) {
    showError("Bitte einen Namen eingeben.");
    return;
  }

  const form = new FormData();
  form.append("name", name);
  form.append("plan", prepared.blob, preparedName);
  form.append("imgWidth", String(prepared.width));
  form.append("imgHeight", String(prepared.height));
  if (els.building.value) form.append("buildingId", els.building.value);
  if (els.floor.value) form.append("floorId", els.floor.value);

  const ratioText = els.ratio.value.trim();
  if (ratioText) {
    form.append("scaleRatio", ratioText);
    const pxPerMeter = derivePxPerMeter(ratioText, prepared.width, prepared.pdfPagePt);
    if (pxPerMeter) {
      form.append("pxPerMeter", String(pxPerMeter));
      form.append("calibrated", "true");
    }
  }

  els.submit.disabled = true;
  els.submit.textContent = "Wird angelegt…";
  try {
    const project = await api.createProject(form);
    els.dialog.close();
    await renderProjectList();
    onOpen(project.id);
  } catch (err) {
    showError(err instanceof Error ? err.message : "Anlegen fehlgeschlagen.");
    els.submit.disabled = false;
    els.submit.textContent = "Foyer anlegen";
  }
}

/* ----------------------------- utils ----------------------------- */

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}
